import {FC, ReactNode, useEffect, useState} from 'react'
import {gql} from '@apollo/client'
import {Controller, FormProvider, SubmitHandler, useForm} from 'react-hook-form'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {omit} from 'lodash'
import Button from 'jbc-front/components/Button'
import {useLazyQuery, useMutation, useQuery} from 'hooks/graphql'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {ProcedureContext, ReportFormProps} from './ReportForm'
import {useConfirmUpgrade} from './useConfirmUpgrade'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {Panel, Body, Head} from 'components/ui/Panel'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {DateField} from 'components/reactHookForm/DateField'
import {Label} from 'components/forms/Label'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import styles from './R513Report.scss'
import {dateFormat, fullNameFormat} from 'libs/formatter'
import {isAdminSelector} from 'libs/redux'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Select} from 'jbc-front/components/presenters/form/Select'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Note} from 'jbc-front/components/presenters/ui/Note'
import ActionButton from 'jbc-front/components/ActionButton'
import {BoxDouble} from 'components/ui/BoxDouble'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {useSelector} from 'hooks/redux'
import {useNotify} from 'hooks/useNotify'
import {BankSearchModal, Bank, BankBranch} from 'components/modals/BankSearchModal'
import {useDispatch} from 'react-redux'
import {asyncError} from 'store/actions/asyncError'
import {ChildcareMethod} from 'features/procedures/Flow/ChildcareMethodSelect/query'
import {
  PAST_CHILDCARE_PROCEDURES_FOR_REFLECT,
  PastChildcareProceduresForReflectQueryResult,
  PastChildcareProceduresForReflectQueryVariables,
} from 'features/procedures/Flow/ChildcareInfoForm/query'

// @ts-ignore
import {titleMap} from 'procedureStatuses/Show'

const APPLICATION_TMP_DATA_NAME = 'r513_param_set'

const R513_REPORT = gql`
  query R513Report(
    $procedureStatusId: ID!
    $isAdmin: Boolean!
    $loadApplication: Boolean!
    $procedureTmpType: ProcedureTmpType!
  ) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        employee {
          id
          staffCode
          displayFullName
        }
        childcareMethodSelect: procedureTmpDatum(type: $procedureTmpType, name: "childcare_method_select") {
          id
          data
        }
        r513ParamSet {
          id
          startedOn
          reasonForPaymentParentalLeaveSupportAllowance
          spouseInsurancePersonNumber0
          spouseInsurancePersonNumber1
          spouseInsurancePersonNumber2
          spouseChildcareLeaveStartOn
          spouseState
          requestUsePublicMoneyReceivingAccount
          bankCode
          bankName
          bankBranchCode
          bankBranchName
          bankBranchType
          bankNumber
          japanPostBank
          japanPostBankCode
          japanPostBankNumber
          version
          createdAt
          needUpgrade
        }
        r513ParamSetForAdmin: r513ParamSet @include(if: $isAdmin) {
          id
          submitOn
          isApplicantConsented
          ignoreMyNumber
          notes
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
        }
        application: procedureTmpDatum(type: application, name: "${APPLICATION_TMP_DATA_NAME}") @include(if: $loadApplication) {
          id
          type
          name
          data
        }
      }
      employeeFieldGroups {
        id
        code
        permission
      }
    }
  }
`

interface R513ReportQueryVariables {
  procedureStatusId: string
  isAdmin: boolean
  loadApplication: boolean
  procedureTmpType: 'draft' | 'employee_draft'
}

interface R513ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
      }
      childcareMethodSelect?: {
        id: string
        data: {method?: ChildcareMethod}
      }
      r513ParamSet: {
        id?: string
        startedOn?: string
        reasonForPaymentParentalLeaveSupportAllowance?: string
        spouseInsurancePersonNumber0?: string
        spouseInsurancePersonNumber1?: string
        spouseInsurancePersonNumber2?: string
        spouseChildcareLeaveStartOn?: string
        spouseState?: string
        requestUsePublicMoneyReceivingAccount: boolean
        bankCode: string
        bankName: string
        bankBranchCode: string
        bankBranchName: string
        bankBranchType?: 'main' | 'branch'
        bankNumber: string
        japanPostBank: boolean
        japanPostBankCode?: string
        japanPostBankNumber?: string
        version: number
        createdAt?: string
        needUpgrade: boolean
      }
      r513ParamSetForAdmin?: {
        id?: string
        submitOn?: string
        isApplicantConsented?: boolean
        ignoreMyNumber?: boolean
        notes: string
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {
          id: string
        }
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
      }
      application?: {
        id: string
        type: string
        name: string
        data: unknown
      }
    }
    employeeFieldGroups: {
      id: string
      code: string
      permission: 'updatable' | 'browsable' | 'not_display'
    }[]
  }
}

const R513_FETCH_EMPLOYEE_BANK_INFO = gql`
  query r513fetchEmployeeBankInfo($procedureStatusId: ID!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        employee {
          id
          bankCode
          bankName
          bankBranchCode
          bankBranchName
          bankNumber
        }
      }
    }
  }
`

interface R513FetchEmployeeBankInfoQueryVariables {
  procedureStatusId: string
}

interface R513FetchEmployeeBankInfoQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      employee: {
        id: string
        bankCode?: string
        bankName?: string
        bankBranchCode?: string
        bankBranchName?: string
        bankNumber?: string
      }
    }
  }
}

const UPDATE_R513_PARAM_SET = gql`
  mutation UpdateR513ParamSet($input: UpdateR513ParamSetInput!) {
    updateR513ParamSet(input: $input) {
      r513ParamSet {
        id
      }
    }
  }
`

interface UpdateR513ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      startedOn: string
      submitOn?: string | null
      isApplicantConsented?: boolean | null
      ignoreMyNumber?: boolean | null
      reasonForPaymentParentalLeaveSupportAllowance?: string | null
      spouseInsurancePersonNumber0?: string | null
      spouseInsurancePersonNumber1?: string | null
      spouseInsurancePersonNumber2?: string | null
      spouseChildcareLeaveStartOn?: string | null
      spouseState?: string | null
      requestUsePublicMoneyReceivingAccount?: boolean | null
      bankCode?: string | null
      bankName?: string | null
      bankBranchCode?: string | null
      bankBranchName?: string | null
      bankBranchType?: 'main' | 'branch' | null
      bankNumber?: string | null
      japanPostBank?: boolean | null
      japanPostBankCode?: string | null
      japanPostBankNumber?: string | null
      notes?: string | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number | null
    }
  }
}

const UPDATE_APPLICATION_TMP_DATA = gql`
  mutation UpdateApplicationTmpData($input: UpdateProcedureTmpDatumInput!) {
    updateProcedureTmpDatum(input: $input) {
      procedureStatus {
        id
      }
    }
  }
`

interface UpdateApplicationTmpDataQueryVariables {
  input: {
    procedureStatusId: string
    type: 'application'
    name: typeof APPLICATION_TMP_DATA_NAME
    data: Schema
  }
}

const UPDATE_PROCEDURE_STATUS = gql`
  mutation updateProcedureStatus($input: UpdateProcedureStatusInput!) {
    updateProcedureStatus(input: $input) {
      procedureStatus {
        id
        dateOn
      }
    }
  }
`

interface UpdateProcedureStatusQueryVariables {
  input: {
    id: string
    dateOn?: string | null
    initialized?: boolean
  }
}

export const R513Report: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  isConfirm,
  comments,
  renderReject,
}) => {
  const dispatch = useDispatch()
  const {data, loading} = useQuery<R513ReportQueryResult, R513ReportQueryVariables>(R513_REPORT, {
    variables: {
      procedureStatusId,
      isAdmin: procedureContext === 'admin',
      loadApplication: isConfirm || procedureContext === 'mypage',
      procedureTmpType: procedureContext === 'admin' ? 'draft' : 'employee_draft',
    },
    fetchPolicy: 'network-only',
  })
  const procedureStatus = data?.client?.procedureStatus
  const employeeFieldGroups = data?.client?.employeeFieldGroups || []

  const [updateR513ParamSet] = useMutation<unknown, UpdateR513ParamSetQueryVariables>(UPDATE_R513_PARAM_SET)
  const [updateApplicationTmpData] = useMutation<unknown, UpdateApplicationTmpDataQueryVariables>(
    UPDATE_APPLICATION_TMP_DATA
  )
  const [updateProcedureStatus] = useMutation<unknown, UpdateProcedureStatusQueryVariables>(UPDATE_PROCEDURE_STATUS)

  if (loading || !procedureStatus) {
    return null
  }

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      switch (procedureContext) {
        case 'admin':
          await updateR513ParamSet({
            variables: {
              input: {
                procedureStatusId,
                attributes: {
                  startedOn: data.startedOn,
                  reasonForPaymentParentalLeaveSupportAllowance: blankToNull(
                    data.paymentParentalLeaveSupportAllowance.reasonForPaymentParentalLeaveSupportAllowance
                  ),
                  spouseInsurancePersonNumber0: data.paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0,
                  spouseInsurancePersonNumber1: data.paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1,
                  spouseInsurancePersonNumber2: data.paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2,
                  spouseChildcareLeaveStartOn: blankToNull(
                    data.paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn
                  ),
                  spouseState: blankToNull(data.paymentParentalLeaveSupportAllowance.spouseState),
                  requestUsePublicMoneyReceivingAccount:
                    data.directDepositForm.requestUsePublicMoneyReceivingAccount === 'true',
                  bankCode: data.directDepositForm.bankCode,
                  bankName: data.directDepositForm.bankName,
                  bankBranchCode: data.directDepositForm.bankBranchCode,
                  bankBranchName: data.directDepositForm.bankBranchName,
                  bankBranchType: data.directDepositForm.bankBranchType,
                  bankNumber: data.directDepositForm.bankNumber,
                  japanPostBank: data.directDepositForm.japanPostBank === 'true',
                  japanPostBankCode: data.directDepositForm.japanPostBankCode,
                  japanPostBankNumber: data.directDepositForm.japanPostBankNumber,
                  ...(data.remarks && {
                    notes: data.remarks.notes,
                    submitOn: blankToNull(data.remarks.submitOn),
                    isApplicantConsented: !!data.remarks.isApplicantConsented,
                    ignoreMyNumber: !!data.remarks.ignoreMyNumber,
                  }),
                  ...(data.laborConsultantForm && {
                    needLaborConsultant: !!data.laborConsultantForm.needLaborConsultant,
                    laborConsultantCreateDate: blankToNull(data.laborConsultantForm.laborConsultantCreateDate),
                    laborConsultantName: data.laborConsultantForm.laborConsultantName,
                    laborConsultantAgentType: data.laborConsultantForm.laborConsultantAgentType,
                    laborConsultantPrefectureId: data.laborConsultantForm.laborConsultantPrefectureId,
                    laborConsultantPhoneNumber0: data.laborConsultantForm.laborConsultantPhoneNumber0,
                    laborConsultantPhoneNumber1: data.laborConsultantForm.laborConsultantPhoneNumber1,
                    laborConsultantPhoneNumber2: data.laborConsultantForm.laborConsultantPhoneNumber2,
                  }),
                  version: procedureStatus.r513ParamSet.version,
                },
              },
            },
          })
          await updateProcedureStatus({
            variables: {
              input: {
                id: procedureStatusId,
                initialized: true,
              },
            },
          })
          break
        case 'mypage':
          await updateApplicationTmpData({
            variables: {
              input: {
                procedureStatusId,
                type: 'application',
                name: APPLICATION_TMP_DATA_NAME,
                data,
              },
            },
          })
          await updateProcedureStatus({
            variables: {
              input: {
                id: procedureStatusId,
                dateOn: blankToNull(data.startedOn),
                initialized: true,
              },
            },
          })
          break
        default:
          return procedureContext satisfies never // exhaustiveness check
      }
    } catch (err) {
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{titleMap[procedureStatus.procedureType]}</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        onSubmit={onSubmit}
        procedureContext={procedureContext}
        employeeFieldGroups={employeeFieldGroups}
        comments={comments}
        renderReject={renderReject}
      />
    </div>
  )
}

const reasonForPaymentParentalLeaveSupportAllowances = [
  {value: '', label: ''},
  {
    value: 'insured_and_take_leave',
    label:
      '1. 配偶者が雇用保険被保険者であって、出生時育児休業給付金または育児休業給付金が支給される休業を一定の期間に14日以上取得した場合',
  },
  {
    value: 'public_officer_and_take_leave',
    label:
      '2. 配偶者が公務員（雇用保険被保険者である場合を除く。）であって、各種法律に基づく育児休業を一定の期間に14日以上取得した場合',
  },
  {value: 'not_leave', label: '3. 配偶者が、子の出生日の翌日において配偶者の育児休業を要件としない場合に該当する場合'},
]

const spouseStates = [
  {value: '', label: ''},
  {value: 'no_spouse', label: '1. 配偶者がいない'},
  {value: 'no_legal_relationship', label: '2. 配偶者が被保険者の子と法律上の親子関係がない'},
  {value: 'domestic_violence_living_separately', label: '3. 被保険者が配偶者から暴力を受け別居中'},
  {value: 'spouse_is_unemployed', label: '4. 配偶者が無業者'},
  {value: 'spouse_is_self_employed', label: '5. 配偶者が自営業やフリーランスなど雇用される労働者でない'},
  {value: 'spouse_is_on_maternity_leave', label: '6. 配偶者が産後休業中'},
  {value: 'other_reason', label: '7. 1～6以外の理由で配偶者が育児休業をすることができない'},
]

const schema = yup.object({
  startedOn: yup.string().required().dateFormat().label('育児休業等開始年月日'),
  paymentParentalLeaveSupportAllowance: yup.object({
    reasonForPaymentParentalLeaveSupportAllowance: yup.string().notRequired().label('出生後休業支援給付金の支給理由'),
    spouseInsurancePersonNumber0: yup
      .string()
      .notRequired()
      .numeric()
      .matches(/^\d{4}$/, {
        message: ({label}) => `${label}を4桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('配偶者の被保険者番号の1番'),
    spouseInsurancePersonNumber1: yup
      .string()
      .notRequired()
      .numeric()
      .matches(/^\d{6}$/, {
        message: ({label}) => `${label}を6桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('配偶者の被保険者番号の2番'),
    spouseInsurancePersonNumber2: yup
      .string()
      .notRequired()
      .numeric()
      .matches(/^\d$/, {
        message: ({label}) => `${label}を1桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('配偶者の被保険者番号の3番'),
    spouseChildcareLeaveStartOn: yup.string().notRequired().dateFormat().label('配偶者の育児休業開始年月日'),
    spouseState: yup.string().notRequired().label('配偶者の状態'),
  }),
  directDepositForm: yup.object({
    requestUsePublicMoneyReceivingAccount: yup
      .string()
      .notRequired()
      .oneOf(['true', 'false'])
      .label('公金受取口座利用希望'),
    bankCode: yup
      .string()
      .notRequired()
      .matches(/^\d{4}$/, {
        message: ({label}) => `${label}を4桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .test(
        'not-japan-post-bank-code',
        '払渡希望金融機関にゆうちょ銀行を指定する場合、金融機関種別でゆうちょ銀行を選択してください',
        (value) => value !== '9900'
      )
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('銀行コード'),
    bankName: yup
      .string()
      .notRequired()
      .max(255)
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('銀行名'),
    bankBranchCode: yup
      .string()
      .notRequired()
      .matches(/^\d{3}$/, {
        message: ({label}) => `${label}を3桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('支店コード'),
    bankBranchName: yup
      .string()
      .notRequired()
      .max(255)
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('支店名'),
    bankBranchType: yup.string().notRequired().oneOf(['main', 'branch']).label('本店支店区分'),
    bankNumber: yup
      .string()
      .notRequired()
      .numeric()
      .max(7, ({label}) => `${label}を半角数字7桁以下で入力してください`)
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('口座番号'),
    japanPostBank: yup.string().notRequired().oneOf(['true', 'false']).label('金融機関種別'),
    japanPostBankCode: yup
      .string()
      .notRequired()
      .matches(/^\d{5}$/, {
        message: ({label}) => `${label}を5桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .when('japanPostBank', {
        is: 'false',
        then: (schema) => schema.transform(() => null),
      })
      .label('記号'),
    japanPostBankNumber: yup
      .string()
      .notRequired()
      .matches(/^\d{8}$/, {
        message: ({label}) => `${label}を8桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .when('japanPostBank', {
        is: 'false',
        then: (schema) => schema.transform(() => null),
      })
      .label('番号'),
  }),
  remarks: yup
    .object({
      notes: yup.string().notRequired().max(68).label('備考'),
      submitOn: yup.string().notRequired().dateFormat().label('提出年月日'),
      isApplicantConsented: yup.boolean().notRequired().label('申請について同意済み'),
      ignoreMyNumber: yup.boolean().notRequired().label('本人事由によりマイナンバー届出不可'),
    })
    .nullable()
    .when('$procedureContext', {is: 'admin', then: (schema) => schema.required()}),
  laborConsultantForm: laborConsultantSchema
    .nullable()
    .when('$procedureContext', {is: 'admin', then: (schema) => schema.required()}),
})

type Schema = yup.InferType<typeof schema>

const makeDefaultValues = (ps: R513ReportQueryResult['client']['procedureStatus']): Schema => {
  const radioBooleanFormat = (value: boolean | undefined | null): 'true' | 'false' | undefined => {
    if (value === undefined || value === null) return undefined
    return value ? 'true' : 'false'
  }

  if (ps.application?.data && !ps.r513ParamSet.createdAt) {
    const {application, ...withoutApplication} = ps
    const defaultValues = makeDefaultValues(withoutApplication)
    const applicationValues = omit(schema.validateSync(application.data, {strict: true}), [
      'remarks',
      'laborConsultantForm',
    ])
    return {...defaultValues, ...applicationValues}
  }

  const r513 = ps.r513ParamSet
  const r513Admin = ps.r513ParamSetForAdmin

  return {
    startedOn: r513?.startedOn ? dateFormat(r513.startedOn, 'YYYY/MM/DD') : '',
    paymentParentalLeaveSupportAllowance: {
      reasonForPaymentParentalLeaveSupportAllowance: r513.reasonForPaymentParentalLeaveSupportAllowance,
      spouseInsurancePersonNumber0: r513.spouseInsurancePersonNumber0,
      spouseInsurancePersonNumber1: r513.spouseInsurancePersonNumber1,
      spouseInsurancePersonNumber2: r513.spouseInsurancePersonNumber2,
      spouseChildcareLeaveStartOn: r513.spouseChildcareLeaveStartOn
        ? dateFormat(r513.spouseChildcareLeaveStartOn, 'YYYY/MM/DD')
        : '',
      spouseState: r513.spouseState,
    },
    directDepositForm: {
      requestUsePublicMoneyReceivingAccount: radioBooleanFormat(r513.requestUsePublicMoneyReceivingAccount),
      bankCode: r513.bankCode,
      bankName: r513.bankName,
      bankBranchCode: r513.bankBranchCode,
      bankBranchName: r513.bankBranchName,
      bankBranchType: r513.bankBranchType,
      bankNumber: r513.bankNumber,
      japanPostBank: radioBooleanFormat(r513.japanPostBank),
      japanPostBankCode: r513.japanPostBankCode,
      japanPostBankNumber: r513.japanPostBankNumber,
    },
    remarks: r513Admin
      ? {
          notes: r513Admin.notes,
          submitOn: r513Admin.submitOn ? dateFormat(r513Admin.submitOn, 'YYYY/MM/DD') : '',
          isApplicantConsented: r513Admin.isApplicantConsented,
          ignoreMyNumber: r513Admin.ignoreMyNumber,
        }
      : null,
    laborConsultantForm: r513Admin
      ? {
          needLaborConsultant: r513Admin.needLaborConsultant,
          laborConsultantCreateDate: r513Admin.laborConsultantCreateDate
            ? dateFormat(r513Admin.laborConsultantCreateDate, 'YYYY/MM/DD')
            : '',
          laborConsultantName: r513Admin.laborConsultantName || '',
          laborConsultantAgentType: r513Admin.laborConsultantAgentType || undefined,
          laborConsultantPrefectureId: r513Admin.laborConsultantPrefecture?.id || undefined,
          laborConsultantPhoneNumber0: r513Admin.laborConsultantPhoneNumber0 || '',
          laborConsultantPhoneNumber1: r513Admin.laborConsultantPhoneNumber1 || '',
          laborConsultantPhoneNumber2: r513Admin.laborConsultantPhoneNumber2 || '',
        }
      : null,
  }
}

interface FormProps {
  procedureStatus: R513ReportQueryResult['client']['procedureStatus']
  submitText: string
  onSubmit: SubmitHandler<Schema>
  procedureContext: ProcedureContext
  employeeFieldGroups: R513ReportQueryResult['client']['employeeFieldGroups']
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({
  procedureStatus,
  submitText,
  onSubmit,
  procedureContext,
  employeeFieldGroups,
  comments,
  renderReject,
}) => {
  const notify = useNotify()
  const isAdmin = useSelector(isAdminSelector)
  const [isOpenBankSearchModal, setIsOpenBankSearchModal] = useState(false)

  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
    context: {procedureContext},
  })
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: {errors, isSubmitting},
  } = methods

  const reasonForPaymentParentalLeaveSupportAllowance = watch(
    'paymentParentalLeaveSupportAllowance.reasonForPaymentParentalLeaveSupportAllowance'
  )
  const isJapanPostBank = watch('directDepositForm.japanPostBank') === 'true'

  // MethodSelect で「過去の手続き情報を利用する」を選択した場合のみ、画面上部に
  // 「過去の手続きを選択」section を表示する（UI は ChildcareInfoForm の from_past section と同形）
  const isFromPast = procedureStatus.childcareMethodSelect?.data?.method === 'from_past'
  const {data: pastData} = useQuery<
    PastChildcareProceduresForReflectQueryResult,
    PastChildcareProceduresForReflectQueryVariables
  >(PAST_CHILDCARE_PROCEDURES_FOR_REFLECT, {
    variables: {procedureStatusId: procedureStatus.id, forProcedureType: procedureStatus.procedureType},
    skip: !isFromPast,
    fetchPolicy: 'network-only',
  })
  const pastProcedures = pastData?.client?.procedureStatus?.employee.pastChildcareProcedureStatuses ?? []
  const pastOptions = pastProcedures.map((p) => {
    const started = p.procedureChildcareDatum?.startedOn
      ? dateFormat(p.procedureChildcareDatum.startedOn, 'YYYY/MM/DD')
      : ''
    const c = p.procedureChildcareDatum?.procedureChildcareChildDatum
    const childName = c?.lastName && c?.firstName ? fullNameFormat(c.lastName, c.firstName) : ''
    const label = childName ? `育休開始日 ${started}\u{3000}子: ${childName}` : `育休開始日 ${started}`
    return {value: p.id, label}
  })
  const [selectedPastId, setSelectedPastId] = useState<string | undefined>()
  // 初期選択は先頭（最直近）。バックエンドが date_on DESC で返すため先頭が最新。
  const currentPastId = selectedPastId ?? pastProcedures[0]?.id

  // 「手続きから情報を反映する」— R513 は「育児休業等開始年月日」のみ反映
  const handleReflectFromPast = () => {
    const datum = pastProcedures.find((p) => p.id === currentPastId)?.procedureChildcareDatum
    if (!datum) {
      return
    }
    setValue('startedOn', datum.startedOn ? dateFormat(datum.startedOn, 'YYYY/MM/DD') : '', {shouldDirty: true})
  }

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r513ParamSet.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )

  useEffect(() => {
    const options = {shouldTouch: true, shouldDirty: true, shouldValidate: true}
    switch (reasonForPaymentParentalLeaveSupportAllowance) {
      case 'insured_and_take_leave':
        setValue('paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn', '', options)
        setValue('paymentParentalLeaveSupportAllowance.spouseState', '', options)
        break
      case 'public_officer_and_take_leave':
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseState', '', options)
        break
      case 'not_leave':
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn', '', options)
        break
      default:
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn', '', options)
        setValue('paymentParentalLeaveSupportAllowance.spouseState', '', options)
        break
    }
  }, [reasonForPaymentParentalLeaveSupportAllowance, setValue])

  const bankInformationPermission = isAdmin
    ? 'updatable'
    : (employeeFieldGroups.find((efg) => efg.code === 'bank_infomation')?.permission ?? 'not_display')

  const [fetchEmployeeBankInfo, {loading: fetchingEmployeeBankInfo}] = useLazyQuery<
    R513FetchEmployeeBankInfoQueryResult,
    R513FetchEmployeeBankInfoQueryVariables
  >(R513_FETCH_EMPLOYEE_BANK_INFO, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const handleSelectBank = (bank: Bank, bankBranch: BankBranch) => {
    setValue('directDepositForm.bankCode', bank.bankCode, {shouldTouch: true, shouldDirty: true, shouldValidate: true})
    setValue('directDepositForm.bankName', bank.name, {shouldTouch: true, shouldDirty: true, shouldValidate: true})
    setValue('directDepositForm.bankBranchCode', bankBranch.branchCode, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('directDepositForm.bankBranchName', bankBranch.name, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleCopyFromEmployeeBankInfo = async () => {
    const res = await fetchEmployeeBankInfo({
      variables: {procedureStatusId: procedureStatus.id},
    })
    if (res.data) {
      const {bankCode, bankName, bankBranchCode, bankBranchName, bankNumber} = res.data.client.procedureStatus.employee
      if ([bankCode, bankName, bankBranchCode, bankBranchName, bankName].every((v) => !v)) {
        notify('従業員情報に口座情報が登録されていないため、データをコピーできません。', 'error')
        return
      }

      setValue('directDepositForm.bankCode', bankCode || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankName', bankName || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankBranchCode', bankBranchCode || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankBranchName', bankBranchName || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankNumber', bankNumber || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} />
        {comments}

        {isFromPast && (
          <Panel className="u-mb20">
            <Head>過去の手続きを選択</Head>
            <Body className={styles.formBody}>
              <Note>過去に作成した「育休の開始」「出生時育休」から「休業期間」の情報を反映することができます。</Note>
              <Select
                options={pastOptions}
                value={pastOptions.find((option) => option.value === currentPastId)}
                onChange={(option) => setSelectedPastId(option?.value)}
                placeholder="選択してください"
              />
              <div>
                <Button onClick={handleReflectFromPast} disabled={!currentPastId}>
                  手続きから情報を反映する
                </Button>
              </div>
            </Body>
          </Panel>
        )}

        <Panel>
          <Head>出生後休業支援給付金支給申請書</Head>
          <Body className={styles.formBody}>
            <DateField name="startedOn" label="育児休業等開始年月日" required error={errors.startedOn?.message} />

            <div className={styles.field}>
              <Label>支給理由の選択</Label>
              <Controller
                control={control}
                name="paymentParentalLeaveSupportAllowance.reasonForPaymentParentalLeaveSupportAllowance"
                render={({field: {onChange, onBlur, value}}) => (
                  <Select
                    options={reasonForPaymentParentalLeaveSupportAllowances}
                    onChange={({value}) => onChange(value)}
                    onBlur={onBlur}
                    value={reasonForPaymentParentalLeaveSupportAllowances.find((option) => option.value === value)}
                    placeholder="選択してください"
                  />
                )}
              />
            </div>
            <div className={styles.field}>
              <Label>配偶者の被保険者番号</Label>
              <div className={styles.personNumber}>
                <Input
                  className={styles.personNumberSegment1}
                  {...register('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0')}
                  maxLength={4}
                  isError={!!errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber0}
                  disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'insured_and_take_leave'}
                />
                -
                <Input
                  className={styles.personNumberSegment2}
                  {...register('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1')}
                  maxLength={6}
                  isError={!!errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber1}
                  disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'insured_and_take_leave'}
                />
                -
                <Input
                  className={styles.personNumberSegment3}
                  {...register('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2')}
                  maxLength={1}
                  isError={!!errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber2}
                  disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'insured_and_take_leave'}
                />
              </div>
              {(() => {
                const personNumberErrors = [
                  errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber0?.message,
                  errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber1?.message,
                  errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber2?.message,
                ].filter((v) => v !== undefined)
                return (
                  personNumberErrors.length > 0 && (
                    <div>
                      {personNumberErrors.map((error, index) => (
                        <ErrorMessage key={index}>{error}</ErrorMessage>
                      ))}
                    </div>
                  )
                )
              })()}
            </div>
            <DateField
              name="paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn"
              label="配偶者の育児休業開始年月日"
              error={errors.paymentParentalLeaveSupportAllowance?.spouseChildcareLeaveStartOn?.message}
              disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'public_officer_and_take_leave'}
            />
            <div className={styles.field}>
              <Label>配偶者の状態</Label>
              <Controller
                control={control}
                name="paymentParentalLeaveSupportAllowance.spouseState"
                render={({field: {onChange, onBlur, value}}) => (
                  <Select
                    options={spouseStates}
                    onChange={({value}) => onChange(value)}
                    onBlur={onBlur}
                    value={spouseStates.find((option) => option.value === value)}
                    placeholder="選択してください"
                    disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'not_leave'}
                  />
                )}
              />
            </div>

            <Panel>
              <Head>払渡希望金融機関指定届</Head>
              <Body className={styles.formBody}>
                <div className={styles.field}>
                  <Label>公金受取口座利用希望</Label>
                  <RadioContainer>
                    <Radio {...register('directDepositForm.requestUsePublicMoneyReceivingAccount')} value="false">
                      希望しない
                    </Radio>
                    <Radio {...register('directDepositForm.requestUsePublicMoneyReceivingAccount')} value="true">
                      希望する
                    </Radio>
                  </RadioContainer>
                  <p className={styles.noticeRed}>
                    マイナポータルに登録されている公金受取口座への振込を新たに希望される場合は「希望する」を選択してください。
                    <br />
                    「希望する」を選択した場合は、以下の払渡希望金融機関の入力が不要ですが、入力された場合は入力された払渡希望金融機関への振込が優先されます。
                  </p>
                </div>
                <div className={styles.field}>
                  <Label>金融機関種別</Label>
                  <RadioContainer>
                    <Radio {...register('directDepositForm.japanPostBank')} value="false">
                      ゆうちょ銀行以外
                    </Radio>
                    <Radio {...register('directDepositForm.japanPostBank')} value="true">
                      ゆうちょ銀行
                    </Radio>
                  </RadioContainer>
                  <p className={styles.noticeRed}>
                    払渡希望金融機関にゆうちょ銀行を指定する場合は記号（5桁）番号（8桁）の形式で入力してください。
                  </p>
                </div>
                {isJapanPostBank ? (
                  <>
                    <BoxDouble>
                      <div className={styles.field}>
                        <Label>記号</Label>
                        <Input
                          {...register('directDepositForm.japanPostBankCode')}
                          isError={!!errors.directDepositForm?.japanPostBankCode}
                          maxLength={5}
                        />
                        {errors.directDepositForm?.japanPostBankCode?.message && (
                          <ErrorMessage>{errors.directDepositForm.japanPostBankCode.message}</ErrorMessage>
                        )}
                      </div>
                      <div className={styles.field}>
                        <Label>番号</Label>
                        <Input
                          {...register('directDepositForm.japanPostBankNumber')}
                          isError={!!errors.directDepositForm?.japanPostBankNumber}
                          maxLength={8}
                        />
                        {errors.directDepositForm?.japanPostBankNumber?.message && (
                          <ErrorMessage>{errors.directDepositForm.japanPostBankNumber.message}</ErrorMessage>
                        )}
                      </div>
                    </BoxDouble>
                    <p className={styles.notice}>
                      店名または店番・口座番号から記号・番号を検索したい場合は
                      <a
                        className="u-txt-link"
                        href="https://www.jp-bank.japanpost.jp/kojin/sokin/furikomi/kouza/kj_sk_fm_kz_2.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        こちら
                      </a>
                    </p>
                  </>
                ) : (
                  <>
                    <div>
                      <ActionButton className="u-mr20" onClick={() => setIsOpenBankSearchModal(true)}>
                        銀行コード検索
                      </ActionButton>
                      {bankInformationPermission !== 'not_display' && (
                        <ActionButton onClick={handleCopyFromEmployeeBankInfo} disabled={fetchingEmployeeBankInfo}>
                          従業員情報からコピー
                        </ActionButton>
                      )}

                      <BankSearchModal
                        isOpen={isOpenBankSearchModal}
                        hideModal={() => setIsOpenBankSearchModal(false)}
                        onSelect={handleSelectBank}
                      />
                    </div>
                    <BoxDouble>
                      <div className={styles.field}>
                        <Label>銀行コード</Label>
                        <Input
                          {...register('directDepositForm.bankCode')}
                          isError={!!errors.directDepositForm?.bankCode}
                        />
                        {errors.directDepositForm?.bankCode?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankCode.message}</ErrorMessage>
                        )}
                      </div>
                      <div className={styles.field}>
                        <Label>銀行名</Label>
                        <Input
                          {...register('directDepositForm.bankName')}
                          isError={!!errors.directDepositForm?.bankName}
                        />
                        {errors.directDepositForm?.bankName?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankName.message}</ErrorMessage>
                        )}
                      </div>
                    </BoxDouble>
                    <BoxDouble>
                      <div className={styles.field}>
                        <Label>支店コード</Label>
                        <Input
                          {...register('directDepositForm.bankBranchCode')}
                          isError={!!errors.directDepositForm?.bankBranchCode}
                        />
                        {errors.directDepositForm?.bankBranchCode?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankBranchCode.message}</ErrorMessage>
                        )}
                      </div>
                      <div className={styles.field}>
                        <Label>支店名</Label>
                        <Input
                          {...register('directDepositForm.bankBranchName')}
                          isError={!!errors.directDepositForm?.bankBranchName}
                        />
                        {errors.directDepositForm?.bankBranchName?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankBranchName.message}</ErrorMessage>
                        )}
                      </div>
                    </BoxDouble>
                    <div className={styles.field}>
                      <Label>本店支店区分</Label>
                      <RadioContainer>
                        <Radio {...register('directDepositForm.bankBranchType')} value="main">
                          本店
                        </Radio>
                        <Radio {...register('directDepositForm.bankBranchType')} value="branch">
                          支店
                        </Radio>
                      </RadioContainer>
                    </div>
                    <div className={styles.field}>
                      <Label>口座番号</Label>
                      <Input
                        {...register('directDepositForm.bankNumber')}
                        isError={!!errors.directDepositForm?.bankNumber}
                      />
                      {errors.directDepositForm?.bankNumber?.message && (
                        <ErrorMessage>{errors.directDepositForm.bankNumber.message}</ErrorMessage>
                      )}
                    </div>
                  </>
                )}
              </Body>
            </Panel>

            {procedureContext === 'admin' && (
              <>
                <Panel>
                  <Head>備考</Head>
                  <Body className={styles.formBody}>
                    <div className={styles.field}>
                      <Label>備考欄</Label>
                      <Textarea {...register('remarks.notes')} isError={!!errors.remarks?.notes} />
                      {errors.remarks?.notes?.message && <ErrorMessage>{errors.remarks.notes.message}</ErrorMessage>}
                    </div>
                  </Body>
                </Panel>

                <Panel>
                  <Head>申請者欄</Head>
                  <Body className={styles.formBody}>
                    <DateField name="remarks.submitOn" label="提出年月日" error={errors.remarks?.submitOn?.message} />

                    <div className={styles.field}>
                      <Label>申請者氏名</Label>
                      <Note>
                        同意書を有する場合等、被保険者の（電子）署名が省略できる場合には以下にチェックを入れてください。
                      </Note>
                      <Checkbox {...register('remarks.isApplicantConsented')}>申請について同意済み</Checkbox>
                    </div>
                  </Body>
                </Panel>

                <div className={styles.field}>
                  <p className="u-mb10">
                    ※ 平成30年5月以降、マイナンバー（個人番号）の記載・添付が必須になります。
                    <br />
                    出力・ダウンロードの前に「マイナンバー出力」ボタンを押すか、印刷後に手でマイナンバーを記入してください。
                  </p>
                  <p>マイナンバーを添付せず電子申請を行う場合以下にチェックを入れてください。</p>
                  <Checkbox {...register('remarks.ignoreMyNumber')}>本人事由によりマイナンバー届出不可</Checkbox>
                </div>

                <LaborConsultantSection
                  procedureStatusId={procedureStatus.id}
                  reportName="R513"
                  prefix="laborConsultantForm."
                />
              </>
            )}
          </Body>
        </Panel>

        <div className="u-ta-c u-mt30">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={confirmUpgrade} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>

      {confirmUpgradeModal}
    </FormProvider>
  )
}
